// tslint:disable: no-use-before-declare
// tslint:disable:max-line-length
import { Observable, Observer, Subject, Subscription } from "rxjs";
import { WebSocketSubject, WebSocketSubjectConfig } from "rxjs/observable/dom/WebSocketSubject";
import { ISubscription } from "rxjs/src/Subscription";
import { Subscribable } from "rxjs/src/Observable";
import { implementsTKeys } from "../../types";
import http from "http";
import * as lodash from "lodash";

/**
 * https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
 */
function generateUUID() { // Public Domain/MIT
    // Time in microseconds since page-load or 0 if unsupported
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        let d = new Date().getTime(); // Timestamp
        let r = Math.random() * 16;
        // random number between 0 and 16
        if (d > 0) {
            // Use timestamp until depleted
            // tslint:disable-next-line:no-bitwise
            r = (d + r) % 16 | 0;
            d = Math.floor(d / 16);
        } else {
            // Use microseconds since page-load if supported
            const { performance } = require("perf_hooks");
            let d2 = (performance && performance.now && (performance.now() * 1000)) || 0;

            // tslint:disable-next-line:no-bitwise
            r = (d2 + r) % 16 | 0;
            d2 = Math.floor(d2 / 16);
        }
        // tslint:disable-next-line:no-bitwise
        return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16);
    });
}

export type TCI_WS_MSG_DESERIALIZER<T> = (e: MessageEvent) => T;
export type TCI_WS_MSG_SERIALIZER<T> = (data: T) => string;

/**
 * WS controller util
 */
export type TCI_WS_CONTROLLER = new (url: string, protocol?: string | Array<string>) => WebSocket;

export interface ITCIWebSocketMessageType {
    /**
     * message type id
     */
    getId();

    /**
     * message type string
     */
    toString();

    /**
     * isEqual
     * @param v
     */
    isEqualTo(v: number): boolean;
}

export class BaseWebsocketMessageType {
    protected constructor(private id: number, private name: string) {
    }

    public static is(v1: any, v2: any): boolean {
        return lodash.isEqual(v1, v2);
    }

    getId() {
        return this.id;
    }

    toString() {
        return this.name;
    }

    public isEqualTo(that: any): boolean {
        // Shortcut for trivial case
        if (this === that) {
            return true;
        }

        // check for primitive
        if (typeof that === "number" && that === this.id) {
            return true;
        }

        // If the that object is not a set, it is not equal to this one.
        // We use instanceof to allow any subclass of Set.
        // We could relax this test if we wanted true duck-typing.
        // Or we could strengthen it to check this.constructor == that.constructor
        // Note that instanceof properly rejects null and undefined values
        if (!(that instanceof BaseWebsocketMessageType)) {
            return false;
        }
        // If two sets don't have the same size, they're not equal

        if (this.toString() !== that.toString()) {
            return false;
        }

        return lodash.isEqual(this, that);
    }
}

/**
 *  Tenant or UseCase will implement its own message types
 */
export class TCIWebsocketMessageType extends BaseWebsocketMessageType implements ITCIWebSocketMessageType {
    public static WS_MSG_TYPE_READY = new TCIWebsocketMessageType(0, "WS Ready");
    public static WS_MSG_TYPE_DISCONNECT = new TCIWebsocketMessageType(1, "WS Disconnect");
    public static WS_MSG_TYPE_DATA = new TCIWebsocketMessageType(2, "WS Data");
    public static WS_MSG_TYPE_ERROR = new TCIWebsocketMessageType(3, "WS Error");
    public static WS_MSG_TYPE_PING = new TCIWebsocketMessageType(4, "WS Ping");
    public static WS_MSG_TYPE_PONG = new TCIWebsocketMessageType(5, "WS Pong");
    public static UNKNOWN = new TCIWebsocketMessageType(6, "unknown");

    private constructor(id: number, name: string) {
        super(id, name);
    }

    public static fromNumber(v: number): TCIWebsocketMessageType {
        v = Number(v);
        switch (v) {
            case TCIWebsocketMessageType.WS_MSG_TYPE_READY.getId():
                return TCIWebsocketMessageType.WS_MSG_TYPE_READY;
            case TCIWebsocketMessageType.WS_MSG_TYPE_DISCONNECT.getId():
                return TCIWebsocketMessageType.WS_MSG_TYPE_DISCONNECT;
            case TCIWebsocketMessageType.WS_MSG_TYPE_DATA.getId():
                return TCIWebsocketMessageType.WS_MSG_TYPE_DATA;
            case TCIWebsocketMessageType.WS_MSG_TYPE_ERROR.getId():
                return TCIWebsocketMessageType.WS_MSG_TYPE_ERROR;
            case TCIWebsocketMessageType.WS_MSG_TYPE_PING.getId():
                return TCIWebsocketMessageType.WS_MSG_TYPE_PING;
            case TCIWebsocketMessageType.WS_MSG_TYPE_PONG.getId():
                return TCIWebsocketMessageType.WS_MSG_TYPE_PONG;
            default:
                return TCIWebsocketMessageType.UNKNOWN;
        }
    }

    /**
     * Parse JSON object to TenantAppType
     * @param jsonObj
     */
    public static fromJSON(jsonObj: any): TCIWebsocketMessageType {
        interface MessageType {
            id: number;
        }

        if (implementsTKeys<MessageType>(jsonObj, ["id"])) {
            return TCIWebsocketMessageType.fromNumber(jsonObj.id);
        } else {
            return TCIWebsocketMessageType.UNKNOWN;
        }
    }
}

/**
 *
 */
export interface ITCIWebSocketMessage<R, M> {
    /**
     * msg id
     */
    id: string;
    /**
     * notification type
     */
    msgType: M;
    /**
     * notification data
     */
    data?: R;
}

export interface ITCIWebSocketService {
    /**
     * connect
     * @param url
     * @param reconnectInterval
     * @param reconnectAttempts
     * @param wsController
     * @param wsOptions
     * @param deserializer
     * @param serializer
     */
    connect<R, M, T extends ITCIWebSocketMessage<R, M>>(url: string,
        reconnectInterval: number,  /// pause between connections
        reconnectAttempts: number,  /// number of connection attempts
        wsController: TCI_WS_CONTROLLER,
        wsOptions?: any,
        deserializer?: TCI_WS_MSG_DESERIALIZER<T>,
        serializer?: TCI_WS_MSG_SERIALIZER<T>): ITCIWebsocketReconnectSubject<R, M, T>;

    /**
     * get connection
     * @param url
     */
    getConnection<R, M, T extends ITCIWebSocketMessage<R, M>>(url: string): ITCIWebsocketReconnectSubject<R, M, T>;

    /**
     * disconnect a connection instance
     * @param id
     */
    disconnect(id: string): void;
}

/**
 * Abstract class AbstractWebSocketService
 * Note: "log" method is static
 */
export abstract class AbstractTCIWebSocketService implements ITCIWebSocketService {
    protected static instance: AbstractTCIWebSocketService;
    protected static connections: { [url: string]: ITCIWebsocketReconnectSubject<any, any, ITCIWebSocketMessage<any, any>> } = {};

    /**
     * Returns a connection by its id
     * @param id
     */
    public static getConnectionById(id: string) {
        return AbstractTCIWebSocketService.connections[id];
    }

    /**
     * Returns the connections collection
     */
    public static getAllConnections(): { [url: string]: ITCIWebsocketReconnectSubject<any, any, ITCIWebSocketMessage<any, any>> } {
        return AbstractTCIWebSocketService.connections;
    }

    /**
     * Start a ping loop on given connection
     * @param connection
     * @param intervalms Ping interval in miliseconds
     */
    public static startPing(
        connection: ITCIWebsocketReconnectSubject<any, any, any>,
        intervalms: number,
        loggerFunction?: (message: string, ...args) => void
    ) {
        loggerFunction = loggerFunction ? loggerFunction : (message, args) => console.log(message, ...args);
        let subscription: Subscription;
        loggerFunction("ws: Starting ping");
        subscription = Observable
            .timer(intervalms, intervalms)
            .subscribe(() => {
                try {
                    connection.sendMsg({
                        id: null,
                        msgType: TCIWebsocketMessageType.WS_MSG_TYPE_PING
                    });
                } catch (e) {  // It got closed
                    loggerFunction("ws: Stopping ping");
                    subscription.unsubscribe();
                }
            });
    }

    /**
     * connect
     * @param url
     * @param reconnectInterval
     * @param reconnectAttempts
     * @param wsController
     * @param wsOptions
     * @param deserializer
     * @param serializer
     */
    public connect<R, M, T extends ITCIWebSocketMessage<R, M>>(
        url: string,
        reconnectInterval: number = 5000,  /// pause between connections
        reconnectAttempts: number = 10,  /// number of connection attempts
        wsController: TCI_WS_CONTROLLER,
        wsOptions?: any,
        deserializer?: TCI_WS_MSG_DESERIALIZER<T>,
        serializer?: TCI_WS_MSG_SERIALIZER<T>,
        loggerFunction?: (message: string, ...args) => void
    ): ITCIWebsocketReconnectSubject<R, M, T> {
        const id = generateUUID();
        const connection = this.create(
            id,
            {
                url,
                reconnectInterval,
                reconnectAttempts,
                wsController,
                wsOptions,
                deserializer,
                serializer,
                loggerFunction
            }
        );

        const subscription = connection.subscribe(
            next => { },
            error => {
                loggerFunction("Connection error", error);
            },
            () => {
                delete AbstractTCIWebSocketService.connections[id];
                subscription.unsubscribe();
            }
        );

        AbstractTCIWebSocketService.connections[id] = connection;
        return connection;
    }

    protected create<R, M, T extends ITCIWebSocketMessage<R, M>>(
        id: string,
        options: {
            url: string,
            reconnectInterval: number,  /// pause between connections
            reconnectAttempts: number,  /// number of connection attempts
            wsController: TCI_WS_CONTROLLER,
            wsOptions?: any,
            deserializer?: TCI_WS_MSG_DESERIALIZER<T>,
            serializer?: TCI_WS_MSG_SERIALIZER<T>,
            loggerFunction?: (message: string, ...args) => void
        }
    ): ITCIWebsocketReconnectSubject<R, M, T> {
        return TCIWsReconnectSubject.connect(
            id,
            options.url,
            options.reconnectInterval,
            options.reconnectAttempts,
            options.wsController,
            options.wsOptions,
            options.deserializer,
            options.serializer,
            options.loggerFunction
        );
    }

    /**
     * get connection
     * @param id
     */
    public getConnection<R, M, T extends ITCIWebSocketMessage<R, M>>(id: string): ITCIWebsocketReconnectSubject<R, M, T> {
        return AbstractTCIWebSocketService.connections[id] as any;
    }

    /**
     * disconnect a connection instance
     */
    public disconnect(id: string) {
        if (AbstractTCIWebSocketService.connections[id]) {
            if (!AbstractTCIWebSocketService.connections[id].closed) {
                AbstractTCIWebSocketService.connections[id].disconnect();
            }
            delete AbstractTCIWebSocketService.connections[id];
        }
    }
}

/**
 *  interface IWsReconnectSubject<T>
 */
export interface ITCIWebsocketReconnectSubject<R, M, T extends ITCIWebSocketMessage<R, M>> extends ISubscription, Subscribable<T> {
    /**
     * Observe connection status
     */
    getConnectionStatus(): Observable<boolean>;

    /**
     * Client disconnect
     */
    disconnect(): void;

    /**
     * Client complete
     */
    complete(): void;

    /**
     * Send message
     */
    sendMsg(data: T);

    /**
     * Connection Id
     */
    getId(): string;
}

/**
 * Reconnectable websocket
 * Note: "log" method is static
 */
export class TCIWsReconnectSubject<R, M, T extends ITCIWebSocketMessage<R, M>> extends Subject<T> implements ITCIWebsocketReconnectSubject<R, M, T> {
    protected reconnectionObservable: Observable<number>;
    protected wsSubjectConfig: WebSocketSubjectConfig;
    protected socket: WebSocketSubject<T>;
    protected connectionObserver: Observer<boolean>;
    protected connectionStatus: Observable<boolean>;
    protected allowReconnect = true;

    constructor(
        protected id: string,
        protected url: string,
        protected reconnectInterval: number = 5000,  /// pause between connections
        protected reconnectAttempts: number = 10,  /// number of connection attempts
        protected wsController: TCI_WS_CONTROLLER,
        protected wsOptions?: any,
        protected deserializer?: TCI_WS_MSG_DESERIALIZER<T>,
        protected serializer?: TCI_WS_MSG_SERIALIZER<T>,
        protected loggerFunction?: (message: string, ...args) => void
    ) {
        super();

        if (!loggerFunction) {
            this.loggerFunction = (message, args) => console.log(message, ...args);
        }

        if (!deserializer) {
            this.deserializer = this.defaultDeserializer;
        }

        if (!this.serializer) {
            this.serializer = this.defaultSerializer;
        }

        this.allowReconnect = reconnectAttempts > 0;

        this.connectionStatus = Observable.create(observer => {
            this.connectionObserver = observer;
        }).share().distinctUntilChanged();

        // ws config
        this.wsSubjectConfig = {
            url: url,
            protocol: this.wsOptions,
            resultSelector: this.deserializer as any,
            WebSocketCtor: this.wsController,
            closeObserver: {
                next: (e: CloseEvent) => {
                    this.socket = null;
                    this.connectionObserver.next(false);
                }
            },
            openObserver: {
                next: (e: Event) => {
                    this.connectionObserver.next(true);
                }
            }
        };

        // Remove the WebSocketCtor attribute if is empty
        if (!this.wsController) {
            delete this.wsSubjectConfig.WebSocketCtor;
        }

        // Remove the protocol attribute if is empty
        if (!this.wsOptions) {
            delete this.wsSubjectConfig.protocol;
        }

        this.connect();

        this.connectionStatus.subscribe((isConnected) => {
            if (!this.reconnectionObservable && typeof (isConnected) === "boolean" && !isConnected) {
                this.reconnect();
            }
        });
    }

    /**
     * connect
     * ```
     * open a rxjs websocket with reconnect interval of 5000 milliseconds and 100 reconnect attempts
     * let wsSubject = new NgWsReconnectSubject.connect("wss://url", 5000, 100);
     *
     * sending to backend
     * Observable.fromEvent(sendMsgBtn, 'click').subscribe((e) => {
     * wsSubject.send(data);
     * });
     *
     * Recieving from backend
     * wsSubject.subscribe()
     *
     * also
     *
     * wsSubject.getConnectionStatus().subscribe((isConnected) => {
     * let msg = isConnected? 'Server connected': 'Server disconnected';
     *  do UI handling
     * });
     * ```
     * @param url
     * @param reconnectInterval
     * @param reconnectAttempts
     * @param wsController
     * @param wsOptions
     * @param deserializer
     * @param serializer
     * @param loggerFunction
     */
    public static connect<R, M, T extends ITCIWebSocketMessage<R, M>>(
        id: string,
        url: string,
        reconnectInterval: number = 5000,  /// pause between connections
        reconnectAttempts: number = 10,  /// number of connection attempts
        wsController: TCI_WS_CONTROLLER,
        wsOptions?: any,
        deserializer?: TCI_WS_MSG_DESERIALIZER<T>,
        serializer?: TCI_WS_MSG_SERIALIZER<T>,
        loggerFunction?: (message: string, ...args) => void
    ) {
        if (!loggerFunction) {
            loggerFunction = (message: string, ...args) => console.log(message, ...args);
        }

        const reconnectSubject$ = new TCIWsReconnectSubject(
            id, url, reconnectInterval, reconnectAttempts, wsController,
            wsOptions, deserializer, serializer, loggerFunction
        );

        reconnectSubject$.socket.socket.addEventListener("error", (err: any) => {
            loggerFunction("FlogoWsReconnectSubject Error with code '" + err.code + "': " + err.message);
        });

        reconnectSubject$.socket.socket.addEventListener("close", (event) => {
            let reason: string;
            loggerFunction("ws: closed with code '" + event.code + "' -> at:" + url);

            if (event.code === 1000) {
                reason = "Normal closure, meaning that the purpose for which the connection was established has been fulfilled.";
            } else if (event.code === 1001) {
                reason = "An endpoint is \"going away\", such as a server going down or a browser having navigated away from a page.";
            } else if (event.code === 1002) {
                reason = "An endpoint is terminating the connection due to a protocol error";
            } else if (event.code === 1003) {
                reason = "An endpoint is terminating the connection because it has received a type of data it cannot accept (e.g., an endpoint that understands only text data MAY send this if it receives a binary message).";
            } else if (event.code === 1004) {
                reason = "Reserved. The specific meaning might be defined in the future.";
            } else if (event.code === 1005) {
                reason = "No status code was actually present.";
            } else if (event.code === 1006) {
                reason = "The connection was closed abnormally, e.g., without sending or receiving a Close control frame";
            } else if (event.code === 1007) {
                reason = "An endpoint is terminating the connection because it has received data within a message that was not consistent with the type of the message (e.g., non-UTF-8 [http://tools.ietf.org/html/rfc3629] data within a text message).";
            } else if (event.code === 1008) {
                reason = "An endpoint is terminating the connection because it has received a message that \"violates its policy\". This reason is given either if there is no other sutible reason, or if there is a need to hide specific details about the policy.";
            } else if (event.code === 1009) {
                reason = "An endpoint is terminating the connection because it has received a message that is too big for it to process.";
            } else if (event.code === 1010) { // Note that this status code is not used by the server, because it can fail the WebSocket handshake instead.
                reason = "An endpoint (client) is terminating the connection because it has expected the server to negotiate one or more extension, but the server didn't return them in the response message of the WebSocket handshake. <br /> Specifically, the extensions that are needed are: " + event.reason;
            } else if (event.code === 1011) {
                reason = "A server is terminating the connection because it encountered an unexpected condition that prevented it from fulfilling the request.";
            } else if (event.code === 1015) {
                reason = "The connection was closed due to a failure to perform a TLS handshake (e.g., the server certificate can't be verified).";
            } else {
                reason = "Unknown reason";
            }
            loggerFunction("websockets closed with reason: " + reason);
        });

        return reconnectSubject$;
    }

    protected defaultDeserializer: TCI_WS_MSG_DESERIALIZER<T> = (evt: MessageEvent): T => {
        if (this.isJSON(evt.data)) {
            const data = JSON.parse(evt.data) as ITCIWebSocketMessage<any, any>;
            return data as any;
        } else {
            this.loggerFunction("ws: Cannot parse JSON data" + JSON.stringify(evt.data));
            return null;
        }
    }

    protected defaultSerializer: TCI_WS_MSG_SERIALIZER<T> = (data: T) => {
        return JSON.stringify(data);
    }

    protected isJSON(str: string): boolean {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return true;
    }

    public getConnectionStatus(): Observable<boolean> {
        return this.connectionStatus;
    }

    protected connect(): void {
        this.socket = WebSocketSubject.create(this.wsSubjectConfig);
        this.socket.subscribe(
            msg => {
                // there is no need to deserialize
                // as the WS msgs gets deserialized by the
                // deserializer passed to the rxjs WebSocketSubject
                if (!this.closed) {
                    return this.next(msg);
                } else if (JSON.stringify(msg).trim().length > 0) {
                    this.loggerFunction("ws: connection closed and message not sent > " + JSON.stringify(msg));
                }
            },
            err => {
                if (!this.socket) {
                    this.reconnect();
                }
            });
    }

    protected reconnect(): void {
        if (!this.allowReconnect) {
            this.closed = true;
            try { this.complete(); } catch (e) { }
            try { this.connectionObserver.complete(); } catch (e) { }
            return;
        }

        this.loggerFunction("ws: Reconnecting");
        this.reconnectionObservable =
            Observable.interval(this.reconnectInterval)
                .takeWhile((v, idx) => {
                    return idx < this.reconnectAttempts && !this.socket;
                });
        this.reconnectionObservable.subscribe(
            () => {
                this.connect();
            },
            null,
            () => {
                this.closed = true;
                this.reconnectionObservable = null;
                if (!this.socket) {
                    try { this.complete(); } catch (e) { }
                    this.connectionObserver.complete();
                }
            }
        );
    }

    public disconnect(): void {
        this.allowReconnect = false;
        this.closed = true;
        const msg = {
            id: generateUUID(),
            msgType: TCIWebsocketMessageType.WS_MSG_TYPE_DISCONNECT,
            data: null
        };
        this.sendMsg(msg as any);

        // So many try-catch because the connection could be already be closed due to external events
        try { this.complete(); } catch (e) { }
        try { this.connectionObserver.complete(); } catch (e) { }
        try { this.socket.unsubscribe(); } catch (e) { }
        try { this.socket.complete(); } catch (e) { }
    }

    public sendMsg(data: T) {
        if (!this.socket || !this.socket.socket) {
            throw new Error("The websocket instance is not present");
        } else if (lodash.isObject(data) || lodash.isString(data)) {
            this.socket.socket.send(this.serializer(data));
        } else {
            throw new Error("The websocket message should be object or string");
        }
    }

    public getId() {
        return this.id;
    }
}

/**
 * ITCIWsClientConnection
 * A Client connection holds the websocket, the client information and ws url on which
 * different data type objects will be transmitted back
 * each express ws url handler is a separate channel for a data type
 */
export interface ITCIWsClientConnection<T, W, U> {
    /**
     * id : { userid:gsbcid:browser_session:path}
     */
    getId(): string;

    /**
     * websocket url of this connection
     */
    getUrl(): string;

    /**
     * user information making this websocket connection
     */
    getUser(): U;

    /**
     * raw websocket connection object
     */
    getWebsocket(): W;

    /**
     * Subject on which the backend will read/write to be transmitted to the client using raw websocket
     * e.g.  the monitoring callback endpoints which listen to AO push req/res will
     * 1. Go to NodeWsConnectionManager.instance to find the handler for incoming req (key => {user,gsbc} )
     * 2. Get the handler getClientConnection().wsSubject
     * 3. Write to the Subject a ITCIWebSocketMessage<R> where R is the particular channel data type
     *    enclosed inside common ITCIWebSocketMessage envelope interface
     * 4. The handler code will listen to this subject and push the ITCIWebSocketMessage<R> message
     *    to the raw {this.websocket} which goes to the UI using this data
     */
    getSendSubject(): Subject<T>;

    /**
     * Subject on which messages will received
     */
    getRecvSubject(): Subject<T>;

    /**
     * Serialize WS msgs to String
     */
    getSerializer(): TCI_WS_MSG_SERIALIZER<T>;

    /**
     * DeSerialize msg string to data object
     */
    getDeserializer(): TCI_WS_MSG_DESERIALIZER<T>;
}

export abstract class AbstractTCIWsConnection<T, W, U> implements ITCIWsClientConnection<T, W, U> {

    /**
     * constructor
     * @param id
     * @param wsUrl
     * @param wsUser
     * @param websocket
     * @param wsController
     * @param wsSendSubject
     * @param wsRecvSubject
     * @param wsOptions
     * @param serializer
     * @param deserializer
     */
    constructor(
        public id: string,
        public wsUrl: string,
        public wsUser: U,
        public websocket: W,
        public wsController: TCI_WS_CONTROLLER,
        public wsSendSubject: Subject<T>,
        public wsRecvSubject: Subject<T>,
        public wsOptions?: any,
        public serializer?: TCI_WS_MSG_SERIALIZER<T>,
        public deserializer?: TCI_WS_MSG_DESERIALIZER<T>
    ) {

        if (!this.serializer) {
            this.serializer = this.defaultSerializer;
        }

        if (!this.deserializer) {
            this.deserializer = this.defaultDeserializer;
        }
    }

    protected defaultDeserializer: TCI_WS_MSG_DESERIALIZER<T> = (evt: MessageEvent): T => {
        if (this.isJSON(evt.data)) {
            return JSON.parse(evt.data);
        } else {
            throw Error("ws: Cannot parse JSON data:" + JSON.stringify(evt.data));
        }
    }

    protected defaultSerializer: TCI_WS_MSG_SERIALIZER<T> = (data: T) => {
        return JSON.stringify(data);
    }

    protected isJSON(str: string): boolean {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return true;
    }

    /**
     * id : { userid:gsbcid:browser_session:path}
     */
    getId(): string {
        return this.id;
    }

    /**
     * websocket url of this connection
     */
    getUrl(): string {
        return this.wsUrl;
    }

    /**
     * user information making this websocket connection
     */
    getUser(): U {
        return this.wsUser;
    }

    /**
     * raw websocket connection object
     */
    getWebsocket(): W {
        return this.websocket;
    }

    /**
     * Subject on which the backend will read/write to be transmitted to the client using raw websocket
     * e.g.  the monitoring callback endpoints which listen to AO push req/res will
     * 1. Go to NodeWsConnectionManager.instance to find the handler for incoming req (key => {user,gsbc} )
     * 2. Get the handler getClientConnection().wsSubject
     * 3. Write to the Subject a ITCIWebSocketMessage<R> where R is the particular channel data type
     *    enclosed inside common ITCIWebSocketMessage envelope interface
     * 4. The handler code will listen to this subject and push the ITCIWebSocketMessage<R> message
     *    to the raw {this.websocket} which goes to the UI using this data
     */
    getSendSubject(): Subject<T> {
        return this.wsSendSubject;
    }

    /**
     * Subject on which messages will received
     */
    getRecvSubject(): Subject<T> {
        return this.wsRecvSubject;
    }

    /**
     * Serialize WS msgs to String
     */
    getSerializer(): TCI_WS_MSG_SERIALIZER<T> {
        return this.serializer;
    }

    /**
     * DeSerialize msg string to data object
     */
    getDeserializer(): TCI_WS_MSG_DESERIALIZER<T> {
        return this.deserializer;
    }
}

export class TCIWsConnection<T, W, U> extends AbstractTCIWsConnection<T, W, U> {
    constructor(
        public id: string,
        public wsUrl: string,
        public wsUser: U,
        public websocket: W,
        public wsController: TCI_WS_CONTROLLER,
        public wsOptions?: any,
        public serializer?: TCI_WS_MSG_SERIALIZER<T>,
        public deserializer?: TCI_WS_MSG_DESERIALIZER<T>
    ) {
        super(id, wsUrl, wsUser, websocket, wsController, new Subject<T>(), new Subject<T>(), wsOptions, serializer, deserializer);
    }
}

/**
 * interface ITCIWsConnectionHandler
 */
export interface ITCIWsConnectionHandler<T, M, W, U> {
    /**
     * initialize the handler with client connection
     * @param wsClientConnection
     */
    init(wsClientConnection: ITCIWsClientConnection<T, W, U>);

    /**
     * Get Client Connection
     */
    getClientConnection(): ITCIWsClientConnection<T, W, U>;

    /**
     * Handle 'error' notification from socket
     * @param websocket
     * @param request
     */
    onError(websocket: W, request: http.IncomingMessage);

    /**
     * Handle 'close' notification from socket
     * @param websocket
     * @param request
     */
    onClose(websocket: W, request: http.IncomingMessage);

    // onMessage(websocket: WebSocket, request: http.IncomingMessage);
    /**
     * Handle 'message' event from socket
     * @param message
     */
    onMessage(message: MessageEvent);

    /**
     * send msg on the socket
     * @param msg
     */
    sendMessage(msg: ITCIWebSocketMessage<T, M>);

    /**
     * Handle 'upgrade' notification on socket
     * @param request
     */
    onUpgrade(this: W, request: http.IncomingMessage);

    /**
     * Handle 'open' notification on socket
     */
    onOpen(this: W);

    /**
     * Handle 'ping' notification on socket
     * @param data
     */
    onPing(this: W, data: Buffer);

    /**
     * Handle 'pong' notification on socket
     * @param data
     */
    onPong(this: W, data: Buffer);

    /**
     * Handle 'unexpected-response' notification on socket
     * @param request
     * @param response
     */
    onUnexpectedResponse(this: W, request: http.ClientRequest, response: http.IncomingMessage);

    /**
     * Destroy client connection
     * @param wsClientConnection
     */
    destroy(wsClientConnection: ITCIWsClientConnection<T, W, U>);

    /**
     * Check if handler can handle data
     * @param data
     */
    canHandle(data: T): boolean;

    /**
     * Get Handler Type which may be a string identifier
     * based on application use case
     */
    getType(): string;

    /**
     * Multiplexer calls this function before sendMessage
     * @param data
     */
    prepareMessage(data: T): ITCIWebSocketMessage<T, M>;
}

/**
 * ITCIWsConnectionManager
 */
export interface ITCIWsConnectionManager {
    /**
     * Connect to the WS handler
     * @param wsHandler
     * @param websocket
     * @param req
     * @param wsController
     * @param wsOptions
     * @param serializer
     * @paraman deserializer
     */
    connect<T, M, W, Rq, U>(
        wsHandler: ITCIWsConnectionHandler<T, M, W, U>,
        websocket: W,
        req: Rq,
        wsController: TCI_WS_CONTROLLER,
        wsOptions: any,
        serializer: TCI_WS_MSG_SERIALIZER<T>,
        deserializer: TCI_WS_MSG_DESERIALIZER<T>): ITCIWsConnectionHandler<T, M, W, U>;

    /**
     * Get list of WS handler connections
     */
    getConnections<T, M, W, U>(): { [id: string]: ITCIWsConnectionHandler<T, M, W, U> };

    /**
     * Disconnect a WS connection
     * @param wsClientConnection
     */
    disconnect(wsClientConnection: ITCIWsClientConnection<any, any, any>): void;

    /**
     * Get ConnectionId string
     * @param userInfo
     * @param wsUrl
     * @param path
     */
    getConnectionId<U>(userInfo: U, wsUrl: string, path: string): string;

    /**
     * Broadcast message to all WS handler connections
     * @param message
     * @param options
     */
    broadcast<T, M>(
        message: ITCIWebSocketMessage<T, M>,
        options?: { mask?: boolean; binary?: boolean; compress?: boolean; fin?: boolean }): void;

    /**
     * Shutdown all WS handler connections
     */
    shutdown(): void;
}
export abstract class AbstractWsConnectionManager implements ITCIWsConnectionManager {
    protected defaultDeSerializer: TCI_WS_MSG_DESERIALIZER<any> = (evt: MessageEvent): any => {
        if (this.isJSON(evt.data)) {
            return JSON.parse(evt.data);
        } else {
            throw new Error("Cannot parse JSON data");
        }
    }

    protected defaultSerializer: TCI_WS_MSG_SERIALIZER<any> = (data: any) => {
        return JSON.stringify(data);
    }

    protected isJSON(str: string): boolean {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return true;
    }

    /**
     * Connect to the WS handler
     * @param wsHandler
     * @param websocket
     * @param req
     * @param wsController
     * @param wsOptions
     * @param serializer
     * @paraman deserializer
     */
    abstract connect<T, M, W, Rq, U>(
        wsHandler: ITCIWsConnectionHandler<T, M, W, U>,
        websocket: W,
        req: Rq,
        wsController: TCI_WS_CONTROLLER,
        wsOptions: any,
        serializer: TCI_WS_MSG_SERIALIZER<T>,
        deserializer: TCI_WS_MSG_DESERIALIZER<T>): ITCIWsConnectionHandler<T, M, W, U>;

    /**
     * Get list of WS handler connections
     */
    abstract getConnections<T, M, W, U>(): { [id: string]: ITCIWsConnectionHandler<T, M, W, U> };

    /**
     * Disconnect a WS connection
     * @param wsClientConnection
     */
    abstract disconnect(wsClientConnection: ITCIWsClientConnection<any, any, any>): void;

    /**
     * Get ConnectionId string
     * @param userInfo
     * @param wsUrl
     * @param path
     */
    abstract getConnectionId<U>(userInfo: U, wsUrl: string, path: string): string;

    /**
     * Broadcast message to all WS handler connections
     * @param message
     * @param options
     */
    abstract broadcast<T, M>(
        message: ITCIWebSocketMessage<T, M>,
        options?: { mask?: boolean; binary?: boolean; compress?: boolean; fin?: boolean }): void;

    /**
     * Shutdown all WS handler connections
     */
    abstract shutdown(): void;
}

/**
 * AbstractWsHandler
 */
export class AbstractWsHandler<T, M, W, C extends ITCIWsConnectionManager, U> extends Subject<T>
    implements ITCIWsConnectionHandler<T, M, W, U> {
    protected wsClientConnection: ITCIWsClientConnection<any, any, any>;

    protected constructor(protected type: string, protected connectionManager: C) {
        super();
    }


    public init(wsClientConnection: ITCIWsClientConnection<T, W, U>) {
        this.wsClientConnection = wsClientConnection;


        // We need to try both .on or .addEventListener to see which one works
        // my gut feeling is addEventListener adds to a list of listeners
        // whereas .on is single listener

        // this.wsClientConnection.websocket.on("error", this.onError.bind(this));
        // this.wsClientConnection.websocket.on("close", this.onClose.bind(this));
        // this.wsClientConnection.websocket.on("message", this.onMessage.bind(this));
        // this.wsClientConnection.websocket.on("upgrade", this.onUpgrade.bind(this));
        // this.wsClientConnection.websocket.on("open", this.onOpen.bind(this));
        // this.wsClientConnection.websocket.on("ping", this.onPing.bind(this));
        // this.wsClientConnection.websocket.on("pong", this.onPong.bind(this));
        // this.wsClientConnection.websocket.on("unexpected-response", this.onUnexpectedResponse.bind(this));

        this.wsClientConnection.getWebsocket().addEventListener("error", this.onError.bind(this));
        this.wsClientConnection.getWebsocket().addEventListener("close", this.onClose.bind(this));
        this.wsClientConnection.getWebsocket().addEventListener("message", this.onMessage.bind(this));
        this.wsClientConnection.getWebsocket().addEventListener("upgrade", this.onUpgrade.bind(this));
        this.wsClientConnection.getWebsocket().addEventListener("open", this.onOpen.bind(this));
        this.wsClientConnection.getWebsocket().addEventListener("ping", this.onPing.bind(this));
        this.wsClientConnection.getWebsocket().addEventListener("pong", this.onPong.bind(this));
        this.wsClientConnection.getWebsocket().addEventListener("unexpected-response",
            this.onUnexpectedResponse.bind(this));
        // https://github.com/ReactiveX/rxjs/blob/master/spec/observables/bindCallback-spec.ts
        // function callback(datum: number, cb: Function) {
        //     cb(datum);
        // }
        //
        // const boundCallback = bindCallback(
        //     callback,
        //     (datum: any) => datum + 1,
        // );
        const sendCallback =
            Observable.bindNodeCallback<T>(
                this.wsClientConnection.getWebsocket().send.bind(this.wsClientConnection.getWebsocket()),
                (data: T) => data
            );
        this.wsClientConnection
            .getSendSubject()
            .flatMap(data => {
                if (lodash.isObject(data) || lodash.isString(data)) {
                    data = this.wsClientConnection.getSerializer()(data);
                } else {
                    return Observable.throw("The websocket message should be object or string");
                }
                return sendCallback(data);
            })
            .subscribe(
                res => { },
                err => { },
                () => { }
            );
    }

    public getClientConnection(): ITCIWsClientConnection<T, W, U> {
        return this.wsClientConnection;
    }

    public onError(websocket: W, request: http.IncomingMessage) {
        this.connectionManager.disconnect(this.wsClientConnection);
    }

    public onClose(websocket: W, request: http.IncomingMessage) {
        this.connectionManager.disconnect(this.wsClientConnection);
    }

    // onMessage(id: string, message: any) {
    public onMessage(msg: MessageEvent) {
        const data = this.wsClientConnection.getDeserializer()(msg) as ITCIWebSocketMessage<any, any>;
        this.wsClientConnection.getRecvSubject().next(data);
    }

    /**
     * send msg on the socket
     * @param msg
     */
    public sendMessage(msg: ITCIWebSocketMessage<T, M>) {
        if (this.wsClientConnection.getSendSubject()) {
            this.wsClientConnection.getSendSubject().next(msg);
        }
    }

    public onUpgrade(this: W, request: http.IncomingMessage) {

    }

    public onOpen(this: W) {

    }

    public onPing(this: W, data: Buffer) {

    }

    public onPong(this: W, data: Buffer) {

    }

    public onUnexpectedResponse(this: W, request: http.ClientRequest, response: http.IncomingMessage) {

    }

    public destroy(wsClientConnection: ITCIWsClientConnection<T, W, U>) {

    }

    public canHandle(data: T): boolean {
        return null;
    }

    public prepareMessage(data: T): ITCIWebSocketMessage<T, M> {
        return null;
    }

    public getType(): string {
        return this.type;
    }
}

/**
 * Web socket service for backend implementation
 */
export class TCIWebSocketBackEndService extends AbstractTCIWebSocketService {
    static getInstance() {
        if (!AbstractTCIWebSocketService.instance) {
            AbstractTCIWebSocketService.instance = new TCIWebSocketBackEndService();
        }
        return AbstractTCIWebSocketService.instance;
    }

    constructor() {
        super();
    }
}

/**
 * Abstract Web sockets connection handler
 */
export abstract class AbstractTCIWsHandler<T, M, W, C extends ITCIWsConnectionManager, U> extends AbstractWsHandler<T, M, W, C, U> { }

/**
 * ITCIMultiplexerConnection
 * Stores the interconnection of two web sockets intances
 */
export interface ITCIMultiplexerConnection<R, M> {

    /**
     * Do the binding of the events and messages from client to server and viceversa.
     * It also handles the closing events to be propagated from one side to the other.
     */
    join(): void;

    /**
     * Closes both client and server connections and send the event back to the client's
     * client (browser)
     */
    break(): void;
}

/**
 * TCI Multiplexer Connection Handler
 * This class is intended to handle the intercommunication between the client connection and the
 * backend connection.
 * Client incoming messages would be passed to backend, and backend responses would be emited back
 * to the browser using the client connection.
 * Errors from client would be detected and close the backend connection. The same in viceversa.
 */
export class TCIMultiplexerConnectionHandler<R, M, T extends ITCIWebSocketMessage<R, M>, W extends WebSocket, C extends ITCIWsConnectionManager, U> implements ITCIMultiplexerConnection<R, M> {
    private closeSubject$: Subject<boolean> = new Subject();
    private clientIsOpen = false;
    private serverIsOpen = false;
    private closeEventSent = false;
    private subscriptions: Subscription[] = [];

    constructor(
        public client: AbstractTCIWsHandler<T, M, W, C, U>,
        public server: ITCIWebsocketReconnectSubject<R, M, T>,
        public id: string,
        public loggerFunction: (message: string, ...args) => void
    ) {
        if (!loggerFunction) {
            this.loggerFunction = (message, args) => console.log(message, ...args);
        }
        this.loggerFunction("ws: Multiplex Handler created with id: " + id);
    }

    join() {
        this.loggerFunction("ws: Multiplex joining...");

        this.serverBindOnMessage();
        this.serverBindStatus();
        this.clientBindOnMessage();
        this.clientBindPipeEvents();
        return this;
    }

    // Close the server when the client closes
    private clientBindPipeEvents() {
        this.client.getClientConnection().getWebsocket()
            .addEventListener("open", () => this.clientIsOpen = true);

        this.client.getClientConnection().getWebsocket()
            .addEventListener("close", () => {
                this.loggerFunction("ws: CLIENT > Got closed");
                this.clientIsOpen = false;

                if (!this.server.closed) {
                    this.server.disconnect();
                }
                this.emitClose();
            });
    }

    // Pipe the incoming message to the server
    private clientBindOnMessage() {
        this.subscriptions.push(
            this.client.getClientConnection().getRecvSubject()
                .subscribe(
                    res => {
                        this.loggerFunction("ws: CLIENT > Message received");
                        if (this.serverIsOpen) {
                            this.loggerFunction("ws: CLIENT > Sending this message to the server");
                            this.server.sendMsg(res);
                        } else {
                            this.loggerFunction("ws: CLIENT > Won't send the message as the server is closed");
                        }
                    },
                    () => { },
                    () => {
                        this.loggerFunction("ws: The connection is closed");
                        this.clientIsOpen = false;
                        this.emitClose();
                    }
                )
        );
    }

    private serverBindStatus() {
        const close = () => {
            this.serverIsOpen = false;
            this.emitClose();
        };

        this.subscriptions.push(
            this.server.getConnectionStatus()
                .subscribe(
                    serverStatus => {
                        this.loggerFunction("ws: server is opened");
                        this.serverIsOpen = serverStatus;

                        // Send READY message
                        // const msg: ITCIWebSocketMessage<T, any> = {
                        //     id: null,
                        //     msgType: TCIWebsocketMessageType.WS_MSG_TYPE_READY,
                        //     data: null
                        // };
                        // this.client.sendMessage(msg);

                        // The server should notify "READY"
                    },
                    (e) => {
                        this.loggerFunction("ws: server connection thrown error", e);
                        close();
                    },
                    () => {
                        this.loggerFunction("ws: server got closed");
                        close();
                    }
                )
        );
    }

    // Receive the server messages and send them back to client
    private serverBindOnMessage() {
        this.subscriptions.push(
            this.server.subscribe((res: any) => {
                this.loggerFunction("ws: SERV > Message received" + JSON.stringify(res));
                this.client.sendMessage(res);
            }) as any
        );
    }

    /**
     * Will emmit "true" when the connection is closed
     */
    public getCloseSubject() {
        return this.closeSubject$;
    }

    private emitClose() {
        // if (!this.clientIsOpen && !this.serverIsOpen && !this.closeEventSent) {
        if (!this.closeEventSent) {
            this.loggerFunction("ws: Multiplexer > Emitting close event");
            this.break();
            this.closeSubject$.next(true);
            this.closeEventSent = true;
        }
    }

    public break() {
        const clientWs = this.client.getClientConnection().getWebsocket();
        if (clientWs.readyState !== clientWs.CLOSED) {
            clientWs.close();
        }

        if (!this.server.closed) {
            this.server.disconnect();
            this.server.complete();
        }

        this.subscriptions.forEach(s => s.unsubscribe());
    }
}

/**
 * AbstractTCIConnMultiplexer
 * Initialize the client and server web socket connections and join them
 * Note: "log" method is static
 */
export class TCIConnMultiplexer {
    private static instance: TCIConnMultiplexer;
    private static joinMap: { [key: string]: ITCIMultiplexerConnection<any, any> } = {};

    static getInstance() {
        if (!TCIConnMultiplexer.instance) {
            TCIConnMultiplexer.instance = new TCIConnMultiplexer();
        }
        return TCIConnMultiplexer.instance;
    }

    public static addConnection(
        clientConnSvc: AbstractWsConnectionManager,
        clientConnSvcOptions: {
            websocketHandler: AbstractTCIWsHandler<any, any, any, any, any>
            websocket: WebSocket,
            expressRequest: any,
            wsController: TCI_WS_CONTROLLER,
            wsOptions: any,
            deserializer: TCI_WS_MSG_DESERIALIZER<any>,
            serializer: TCI_WS_MSG_SERIALIZER<any>
        },
        backEndConnSvc: AbstractTCIWebSocketService,
        backEndConnSvcOptions: {
            url: string,
            websocket: WebSocket,
            wsOptions: any,
            deserializer?: TCI_WS_MSG_SERIALIZER<any>,
            serializer?: TCI_WS_MSG_SERIALIZER<any>,
        },
        loggerFunction?: (message: string, ...args) => void
    ) {
        if (!loggerFunction) {
            loggerFunction = TCIConnMultiplexer.log;
        }

        loggerFunction("ws: Multiplexer > Adding Connection");

        const clientConnection = clientConnSvc.connect(
            clientConnSvcOptions.websocketHandler,
            clientConnSvcOptions.websocket,
            clientConnSvcOptions.expressRequest,
            clientConnSvcOptions.wsController,
            clientConnSvcOptions.wsOptions,
            clientConnSvcOptions.serializer,
            clientConnSvcOptions.deserializer
        ) as AbstractTCIWsHandler<any, any, any, any, any>;
        const id = clientConnection.getClientConnection().getId();

        // Clean hand-shake
        delete backEndConnSvcOptions.wsOptions.headers["sec-websocket-extensions"];
        delete backEndConnSvcOptions.wsOptions.headers["sec-websocket-key"];
        delete backEndConnSvcOptions.wsOptions.headers["sec-websocket-version"];

        const backendConn = backEndConnSvc.connect(
            backEndConnSvcOptions.url,
            500,
            0,
            backEndConnSvcOptions.websocket as any,
            backEndConnSvcOptions.wsOptions,
            backEndConnSvcOptions.deserializer,
            backEndConnSvcOptions.serializer,
            loggerFunction
        );

        const multiplex = new TCIMultiplexerConnectionHandler(clientConnection, backendConn, id, loggerFunction);
        const subscription = multiplex
            .join()
            .getCloseSubject()
            .filter(r => !!r)
            .subscribe(() => {
                backEndConnSvc.disconnect(multiplex.client.getClientConnection().getId());
                TCIConnMultiplexer.cleanJoinedConnections(id);
                subscription.unsubscribe();
            });

        TCIConnMultiplexer.joinMap[id] = multiplex;

        loggerFunction("ws: Multiplexer > 'join' have been called");
        return multiplex;
    }

    private static cleanJoinedConnections(joinId: string) {
        if (TCIConnMultiplexer.joinMap[joinId]) {
            delete TCIConnMultiplexer.joinMap[joinId];
        }
    }

    public static getConnections() {
        return TCIConnMultiplexer.joinMap;
    }

    public static closeAllConnections(loggerFunction?: (message: string, ...args) => void) {
        if (!loggerFunction) {
            loggerFunction = TCIConnMultiplexer.log;
        }

        for (const i in TCIConnMultiplexer.joinMap) {
            if (TCIConnMultiplexer.joinMap.hasOwnProperty(i)) {
                loggerFunction("ws: Multiplexer > Closing all connections", i);
                TCIConnMultiplexer.joinMap[i].break();
            }
        }
    }

    /**
     * Log message (console or similar)
     */
    private static log(message: string, ...args) {
        console.log(message, ...args);
    }
}
